import { useContext, useMemo, useState } from "react";
import Table from "../../../Common/Table/Table";
import Button from "../../../Common/Button/Button";
import uploadIcon from "../../../Assets/Images/resume-upload-icon.png";
import CustomTooltip from "../../../Common/Tooltip/Tooltip";
import FileIcon from "../../../Common/Common-Function/IconMapping.js";
import { DynamicContext } from "../../../Context/DynamicContext.js";
import DotLoader from "../../../Common/Pre-Loader/DotLoader.jsx";

const parseJobPreview = (preview) => {
  if (!preview) return {};
  if (typeof preview === "object") return preview;
  try {
    return JSON.parse(preview);
  } catch (error) {
    console.warn("Unable to parse job_preview:", error);
    return {};
  }
};

const normalizeStatus = (status) => (status || "processing").toLowerCase();

function JobXLResults(props) {
  const { isDarkMode } = useContext(DynamicContext);
  const {
    jobResults = props?.JobResults || [],
    setJobResults,
    showHeader = true,
    uploadedFiles = props?.UploadedFiles || [],
    setUploadedFiles,
    failedJobs = props?.failedJobs || [],
    setFailedJobs,
    resetJobState,
  } = props || {};

  const [selectedJob, setSelectedJob] = useState(null);

  const results = Array.isArray(jobResults) ? jobResults : [];
  const files = Array.isArray(uploadedFiles) ? uploadedFiles : [];
  const failures = Array.isArray(failedJobs) ? failedJobs : [];

  const groupedResults = useMemo(() => {
    const grouped = {};
    results.forEach((result) => {
      const filename = result.name || result.file_name || result.filename || "Unknown file";
      if (!grouped[filename]) {
        grouped[filename] = {
          jobs: [],
          statusCounts: { created: 0, updated: 0, incomplete: 0, failed: 0 },
          metadata: result.metadata || {},
        };
      }
      const jobId = result.id || result.job_id || `${filename}-${grouped[filename].jobs.length}`;
      const exists = grouped[filename].jobs.some(
        (item) => (item.id || item.job_id) === jobId,
      );
      if (!exists) {
        grouped[filename].jobs.push(result);
        const status = normalizeStatus(result.job_creation_status);
        if (status === "created") grouped[filename].statusCounts.created += 1;
        else if (status === "updated") grouped[filename].statusCounts.updated += 1;
        else if (status === "incomplete") grouped[filename].statusCounts.incomplete += 1;
        else if (status === "failed" || status === "error") grouped[filename].statusCounts.failed += 1;
      }
      if (result.metadata) grouped[filename].metadata = result.metadata;
    });
    return grouped;
  }, [results]);

  const totalStatusCounts = useMemo(() => {
    const totals = { created: 0, updated: 0, incomplete: 0, failed: 0 };
    Object.values(groupedResults).forEach(({ statusCounts }) => {
      Object.keys(totals).forEach((key) => {
        totals[key] += statusCounts[key];
      });
    });
    failures.forEach((item) => {
      const alreadyIncluded = Object.values(groupedResults).some(({ jobs }) =>
        jobs.some((job) =>
          (item.object_key && job.object_key === item.object_key) ||
          (item.id && job.id === item.id),
        ),
      );
      if (!alreadyIncluded) {
        if (item.isIncomplete) totals.incomplete += 1;
        else totals.failed += 1;
      }
    });
    return totals;
  }, [groupedResults, failures]);

  const tableData = useMemo(() => {
    const rows = [];
    Object.entries(groupedResults).forEach(([filename, fileData]) => {
      fileData.jobs.forEach((job, index) => {
        const preview = parseJobPreview(job.job_preview);
        rows.push({
          id: job.id || job.job_id || `${filename}-${index}`,
          filename,
          job,
          job_id: job.job_id || "",
          job_title: job.job_title || preview.title || preview.job_title || "N/A",
          client_name: preview.client_name || job.client_name || "N/A",
          location: preview.location || job.location || "N/A",
          status: normalizeStatus(job.job_creation_status),
          created_at: job.created_at,
        });
      });
    });
    return rows;
  }, [groupedResults]);

  const sortedUploadedFiles = useMemo(() => {
    return [...files].sort((a, b) => {
      const aName = typeof a === "string" ? a : a.name;
      const bName = typeof b === "string" ? b : b.name;
      const aFailed = failures.some((item) => item.name === aName);
      const bFailed = failures.some((item) => item.name === bName);
      return Number(aFailed) - Number(bFailed);
    });
  }, [files, failures]);

  const handleClearResults = () => {
    if (window.socketRef?.current) {
      window.socketRef.current.close(1000, "User returned to upload page");
      window.socketRef.current = null;
    }
    if (window.countdownIntervalRef?.current) {
      clearInterval(window.countdownIntervalRef.current);
      window.countdownIntervalRef.current = null;
    }
    setJobResults?.([]);
    setUploadedFiles?.([]);
    setFailedJobs?.([]);
    resetJobState?.();
  };

  const columns = [
    {
      name: "#",
      selector: (row, index) => <span>{index + 1}</span>,
      width: "70px",
    },
    {
      name: "Job Title",
      selector: (row) => (
        <CustomTooltip title={row.job_title} arrow>
          <span>{row.job_title}</span>
        </CustomTooltip>
      ),
      width: "300px",
    },
    {
      name: "Client",
      selector: (row) => (
        <CustomTooltip title={row.client_name} arrow>
          <span>{row.client_name}</span>
        </CustomTooltip>
      ),
      width: "180px",
    },
    {
      name: "Location",
      selector: (row) => (
        <CustomTooltip title={row.location} arrow>
          <span>{row.location}</span>
        </CustomTooltip>
      ),
      width: "180px",
    },
    {
      name: "Spreadsheet File",
      selector: (row) => (
        <CustomTooltip title={row.filename} arrow>
          <span className="parsing-file-name-container">
            <FileIcon fileName={row.filename} />
            {row.filename}
          </span>
        </CustomTooltip>
      ),
      width: "230px",
    },
    {
      name: "Status",
      selector: (row) => {
        const color =
          row.status === "created" ? "green" :
          row.status === "updated" ? "#ffc100" :
          row.status === "incomplete" ? "#FF6B6B" :
          row.status === "failed" || row.status === "error" ? "#F44336" : "gray";
        return (
          <span style={{ color, fontSize: "14px", fontWeight: "bold", textTransform: "capitalize" }}>
            {row.status}
          </span>
        );
      },
      width: "125px",
    },
    {
      name: "Actions",
      cell: (row) => (
        <button
          type="button"
          className="parser-table-action-icons"
          title="View extracted job data"
          onClick={() => setSelectedJob(row)}
          style={{
            cursor: "pointer",
            border: 0,
            background: "transparent",
            color: isDarkMode ? "#fff" : "#6860EE",
            fontWeight: 600,
          }}
        >
          View
        </button>
      ),
      width: "100px",
    },
  ];

  const totalJobs = Object.values(groupedResults).reduce(
    (total, fileData) => total + Number(fileData.metadata?.total_jobs || fileData.jobs.length),
    0,
  ) || (totalStatusCounts.created + totalStatusCounts.updated + totalStatusCounts.incomplete + totalStatusCounts.failed);

  const summaryCards = [
    { label: "Total Jobs", value: totalJobs, color: "#6860EE", note: "Records in spreadsheets" },
    { label: "Created", value: totalStatusCounts.created, color: "#4CAF50", note: "New jobs" },
    { label: "Updated", value: totalStatusCounts.updated, color: "#FF9800", note: "Existing jobs" },
    { label: "Incomplete", value: totalStatusCounts.incomplete, color: "#FF6B6B", note: "Partial data extracted" },
    { label: "Failed", value: totalStatusCounts.failed, color: "#F44336", note: "Could not be processed" },
  ];

  return (
    <>
      <div
        className="spreadsheet-summary-container"
        style={{
          background: isDarkMode ? "rgba(30, 30, 40, 0.7)" : "white",
          borderRadius: "12px",
          padding: "20px",
          margin: "20px",
          border: isDarkMode ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,0,0,0.05)",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "20px", flexWrap: "wrap", marginBottom: "20px" }}>
          <div>
            <h3 style={{ color: isDarkMode ? "#fff" : "#6860EE", margin: 0, fontSize: "18px", fontWeight: 600 }}>
              Import Summary
            </h3>
            <p style={{ color: isDarkMode ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.5)", fontSize: "14px", margin: "4px 0 0" }}>
              {files.length} spreadsheet file{files.length === 1 ? "" : "s"} uploaded
            </p>
          </div>
          <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
            {summaryCards.map((card) => (
              <div key={card.label} style={{
                padding: "12px 16px",
                background: isDarkMode ? `${card.color}26` : `${card.color}14`,
                borderRadius: "10px",
                border: `1px solid ${card.color}40`,
                minWidth: "112px",
                textAlign: "center",
              }}>
                <div style={{ fontSize: "12px", fontWeight: 500, color: isDarkMode ? "rgba(255,255,255,0.7)" : "#666", marginBottom: "4px" }}>{card.label}</div>
                <div style={{ fontSize: "24px", fontWeight: 700, color: card.color, lineHeight: 1 }}>{card.value}</div>
                <div style={{ fontSize: "11px", color: isDarkMode ? "rgba(255,255,255,0.65)" : "#666", marginTop: "4px" }}>{card.note}</div>
              </div>
            ))}
          </div>
        </div>

        <div style={{ maxHeight: "180px", overflowY: "auto" }}>
          {sortedUploadedFiles.map((file, index) => {
            const filename = typeof file === "string" ? file : file.name;
            const fileData = groupedResults[filename];
            const failedFile = failures.find((item) => item.name === filename);
            const counts = fileData?.statusCounts || { created: 0, updated: 0, incomplete: 0, failed: 0 };
            const metadata = fileData?.metadata || {};
            const isProcessing = !fileData && !failedFile;
            return (
              <div key={`${filename}-${index}`} style={{
                padding: "12px", marginBottom: "8px",
                background: isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.02)",
                borderRadius: "8px", display: "flex", justifyContent: "space-between",
                alignItems: "center", gap: "12px", flexWrap: "wrap",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: "10px", minWidth: 0 }}>
                  <FileIcon fileName={filename} />
                  <strong style={{ color: isDarkMode ? "#fff" : "#333", fontSize: "14px" }}>{filename}</strong>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                  {["created", "updated", "incomplete", "failed"].map((status) => counts[status] > 0 && (
                    <span key={status} style={{
                      padding: "4px 8px", borderRadius: "6px", fontSize: "12px", fontWeight: 500,
                      color: status === "created" ? (isDarkMode ? "#4CAF50" : "#2E7D32") :
                        status === "updated" ? "#FF9800" :
                        status === "incomplete" ? "#FF6B6B" : "#F44336",
                      background: status === "created" ? "rgba(76,175,80,0.1)" :
                        status === "updated" ? "rgba(255,152,0,0.1)" :
                        status === "incomplete" ? "rgba(255,107,107,0.1)" : "rgba(244,67,54,0.1)",
                    }}>
                      {status[0].toUpperCase() + status.slice(1)}: {counts[status]}
                    </span>
                  ))}
                  {failedFile && <span style={{ color: "#F44336", fontSize: "12px" }}>{failedFile.error || "Processing failed"}</span>}
                  {isProcessing && (
                    <div className="parsed-status-container">
                      <DotLoader size={1} borderWidth={5} duration={1.5} />
                      <span>Processing</span>
                    </div>
                  )}
                  {metadata.total_jobs != null && (
                    <span style={{ color: isDarkMode ? "#bbb" : "#666", fontSize: "12px" }}>
                      {metadata.completed_jobs || 0} / {metadata.total_jobs} completed
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="main-resume-container">
        <div className="table-container">
          <Table
            columns={columns}
            data={tableData}
            pagination
            highlightOnHover
            fixedHeader
            fixedHeaderScrollHeight="500px"
            dataNotFoundMessage="No job import results yet..!"
            clearSelectedRows={false}
            backgroundColor="var(--body__background)"
            color="var(--header__color)"
          />
        </div>
      </div>

      {selectedJob && (
        <div
          role="presentation"
          onClick={() => setSelectedJob(null)}
          style={{
            position: "fixed", inset: 0, zIndex: 1500,
            background: "rgba(0,0,0,0.45)", display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Job details"
            onClick={(event) => event.stopPropagation()}
            style={{
              width: "min(560px, 92vw)", height: "100%", overflowY: "auto",
              padding: "24px", boxSizing: "border-box",
              background: isDarkMode ? "#242430" : "#fff",
              color: isDarkMode ? "#fff" : "#333",
              boxShadow: "-8px 0 24px rgba(0,0,0,0.2)",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px" }}>
              <h3 style={{ margin: 0 }}>Extracted Job Details</h3>
              <button type="button" onClick={() => setSelectedJob(null)} aria-label="Close job details">✕</button>
            </div>
            <h4 style={{ marginBottom: "16px" }}>{selectedJob.job_title}</h4>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "16px" }}>
              {[
                ["Client", selectedJob.client_name],
                ["Location", selectedJob.location],
                ["Job ID", selectedJob.job_id || "Not created"],
                ["Status", selectedJob.status],
                ["Source File", selectedJob.filename],
              ].map(([label, value]) => (
                <div key={label}>
                  <div style={{ fontSize: "12px", opacity: 0.65, marginBottom: "4px" }}>{label}</div>
                  <div style={{ fontSize: "14px", overflowWrap: "anywhere" }}>{value}</div>
                </div>
              ))}
            </div>
            <h4>Job Preview</h4>
            <pre style={{
              whiteSpace: "pre-wrap", overflowWrap: "anywhere",
              padding: "14px", borderRadius: "8px",
              background: isDarkMode ? "rgba(0,0,0,0.25)" : "#f5f5f8",
              fontSize: "12px",
            }}>
              {JSON.stringify(parseJobPreview(selectedJob.job.job_preview), null, 2)}
            </pre>
          </div>
        </div>
      )}
    </>
  );
}

export default JobXLResults;